import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { CoachState } from "@/providers/walter-coach";
import { CoachSmartCatalogPayload, SmartCatalogOptions, SmartCatalogOverridePayload } from "@/services/walter-assistant";
import { ChevronDown, GripHorizontal, X } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../ui/button";
import { COACH_SMART_CATALOG_DRAWER_WIDTH, useDrawerLayout } from "../use-drawer-layout";
import { SmartCatalogPanel } from "./smart-catalog-panel";

const SMART_CATALOG_DRAWER_HEIGHT_KEY = "coach-smart-catalog-drawer-height";
const SMART_CATALOG_DRAWER_LEFT_KEY = "coach-smart-catalog-drawer-left";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    payload: CoachSmartCatalogPayload | null;
    isPending: boolean;
    isVerbatimPending: boolean;
    onRefresh: () => void;
    coachState: CoachState;
    playbookName: string | null | undefined;
    options?: SmartCatalogOptions | null;
    onOverride?: (payload: SmartCatalogOverridePayload) => void;
    onRevealThemes?: (themeCodes: string[]) => void;
};

/**
 * Smart Catalog en drawer flottant (données catalogue brutes, scorées).
 *
 * Vue secondaire vis-à-vis du tableau financeur : sert à fouiller le
 * détail des thèmes / produits. La génération de verbatims (Phase B) est
 * en pause côté backend — le drawer n'affiche que le catalogue brut.
 */
export function SmartCatalogDrawer({
    isOpen,
    onClose,
    payload,
    isPending,
    isVerbatimPending,
    onRefresh,
    coachState,
    playbookName,
    options,
    onOverride,
    onRevealThemes
}: Props) {
    const { height, left, isDragging, onLayoutHandlePointerDown } = useDrawerLayout({
        width: COACH_SMART_CATALOG_DRAWER_WIDTH,
        heightStorageKey: SMART_CATALOG_DRAWER_HEIGHT_KEY,
        leftStorageKey: SMART_CATALOG_DRAWER_LEFT_KEY
    });
    const [isCollapsed, setIsCollapsed] = useState(false);

    if (!isOpen) return null;

    return createPortal(
        <div
            className={cn(
                "fixed bottom-11 z-40 flex flex-col overflow-hidden rounded-t-md border border-b-0 bg-white shadow-lg",
                !isDragging && "transition-[height] duration-200 ease-out"
            )}
            style={{ height: isCollapsed ? 76 : height, left, width: COACH_SMART_CATALOG_DRAWER_WIDTH }}
            role="region"
            aria-label="Smart Catalog"
        >
            <div
                className="flex shrink-0 cursor-move touch-none items-center justify-center border-b bg-gray-50 py-1"
                onPointerDown={onLayoutHandlePointerDown}
                aria-label="Déplacer et redimensionner le Smart Catalog"
            >
                <GripHorizontal className="size-4 text-gray-400" aria-hidden />
            </div>

            <div className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-1.5">
                <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">Smart Catalog</p>
                    <p className="truncate text-xs text-gray-500">{playbookName || "Pas de playbook"}</p>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                    <Button
                        variant="ghost" size="icon" className="size-7"
                        onClick={() => setIsCollapsed(v => !v)}
                        aria-label={isCollapsed ? "Développer" : "Réduire"}
                    >
                        <ChevronDown className={`size-3.5 transition-transform duration-200 ${isCollapsed ? "" : "rotate-180"}`} aria-hidden />
                    </Button>
                    <Button variant="ghost" size="icon" className="size-7" onClick={onClose} aria-label="Fermer le Smart Catalog">
                        <X className="size-3.5" aria-hidden />
                    </Button>
                </div>
            </div>

            {/* contenu — toujours rendu, masqué par overflow-hidden sur le conteneur */}
            <ScrollArea className="min-h-0 flex-1">
                <div className="bg-gray-50 p-4">
                    <SmartCatalogPanel
                        payload={payload}
                        isPending={isPending}
                        isVerbatimPending={isVerbatimPending}
                        onRefresh={onRefresh}
                        coachState={coachState}
                        options={options}
                        onOverride={onOverride}
                        onRevealThemes={onRevealThemes}
                    />
                </div>
            </ScrollArea>
        </div>,
        document.body
    );
}
